import React, { useCallback, useContext, useEffect, useState } from 'react';
import { LookupService } from '../../services';
import {
    IProject, IDirectorate, IPartnerOrganisation, IRisk,
    IReportDueDates, ReportDueDates, IFinancialRisk, IBaseComponentProps, IPortfolio, IProgramme,
    ICustomerFinancialYear
} from '../../types';
import styles from '../../styles/cr.module.scss';
import { SignOffType } from '../../refData/SignOffType';
import { SignOffBuilder } from './SignOffBuilder';
import { CrLoadingOverlay } from '../cr/CrLoadingOverlay';
import { Period } from '../../refData/Period';
import { ReportSelector } from '../ReportSelector';
import { DataContext } from '../DataContext';

interface ISignOffListProps extends IBaseComponentProps {
    defaultReportType?: SignOffType;
    defaultEntityId?: number;
    defaultUpdatePeriod?: Date;
    showPreviousUpdate?: boolean;
    customerFinancialYear?: ICustomerFinancialYear;
    reportPeriod?: Period;    
    //onChangePeriod?: (period: Period) => void;
}

export const SignOffList = (props: ISignOffListProps): React.ReactElement => {
    const { defaultReportType, defaultEntityId, errorHandling: { onError } } = props;
    const { dataServices: { directorateService, portfolioService, programmeService, projectService, partnerOrganisationService,
        corporateRiskService, financialRiskService, reportDueDatesService } } = useContext(DataContext);
    const [loading, setLoading] = useState(false);
    const [directorates, setDirectorates] = useState<IDirectorate[]>(null);
    const [portfolios, setPortfolios] = useState<IPortfolio[]>();
    const [programmes, setProgrammes] = useState<IProgramme[]>();
    const [projects, setProjects] = useState<IProject[]>(null);
    const [partnerOrgs, setPartnerOrgs] = useState<IPartnerOrganisation[]>(null);
    const [risks, setRisks] = useState<IRisk[]>(null);
    const [financialRisks, setFinancialRisks] = useState<IFinancialRisk[]>(null);
    const [hideReportTypeSelector, setHideReportTypeSelector] = useState<boolean>(false);
    const [selectedSignOff, setSelectedSignOff] = useState<{ type: SignOffType, id: number }>({ type: null, id: null });
    const [selectedReportPeriod, setSelectedReportPeriod] = useState<Period>(Period.Current);
    const [selectedSignOffDates, setSelectedSignOffDates] = useState<IReportDueDates>(new ReportDueDates);

    const logError = useCallback(onError, [onError]);

    useEffect(() => {
        if (props.reportPeriod === Period.Current || props.reportPeriod === Period.Previous) {
            //console.log('props.reportPeriod', props.reportPeriod);
            setSelectedReportPeriod(props.reportPeriod);
        }

    }, [props.reportPeriod]);

    useEffect(() => {
        if (defaultReportType && defaultEntityId && !selectedSignOff.type) {
            setHideReportTypeSelector(true);
            setSelectedSignOff({ type: defaultReportType, id: defaultEntityId });
        }
    }, [defaultReportType, defaultEntityId, selectedSignOff.type]);

    useEffect(() => {
        const loadSignOffs = async (): Promise<void> => {
            setLoading(true);
            try {
                await Promise.all([
                    directorateService.readMyDirectorates().then(setDirectorates),
                    portfolioService.readDraftReportPortfolios().then(setPortfolios),
                    programmeService.readDraftReportProgrammes().then(setProgrammes),
                    projectService.readMyProjects().then(setProjects),
                    partnerOrganisationService.readMyPartnerOrganisations().then(setPartnerOrgs),
                    corporateRiskService.readMyRisks().then(setRisks),
                    financialRiskService.readMyRisks().then(setFinancialRisks)
                ]);
            } catch (err) {
                logError(`Error loading sign off options`, err.message);
            } finally {
                setLoading(false);
            }
        };
        loadSignOffs();
    }, [directorateService, projectService, partnerOrganisationService, corporateRiskService, financialRiskService, logError]);

    useEffect(() => {
        // If only one report of any type, select it automatically
        if (directorates && projects && partnerOrgs && risks && financialRisks) {
            if (directorates.length === 1 && projects.length === 0 && partnerOrgs.length === 0 && risks.length === 0 && financialRisks.length === 0)
                setSelectedSignOff({ type: SignOffType.Directorate, id: directorates[0].ID });
            if (directorates.length === 0 && projects.length === 1 && partnerOrgs.length === 0 && risks.length === 0 && financialRisks.length === 0)
                setSelectedSignOff({ type: SignOffType.Project, id: projects[0].ID });
            if (directorates.length === 0 && projects.length === 0 && partnerOrgs.length === 1 && risks.length === 0 && financialRisks.length === 0)
                setSelectedSignOff({ type: SignOffType.PartnerOrganisation, id: partnerOrgs[0].ID });
            if (directorates.length === 0 && projects.length === 0 && partnerOrgs.length === 0 && risks.length === 1 && financialRisks.length === 0)
                setSelectedSignOff({ type: SignOffType.Risk, id: risks[0].ID });
            if (directorates.length === 0 && projects.length === 0 && partnerOrgs.length === 0 && risks.length === 0 && financialRisks.length === 1)
                setSelectedSignOff({ type: SignOffType.FinancialRisk, id: financialRisks[0].ID });
        }
    }, [directorates, projects, partnerOrgs, risks, financialRisks]);

    useEffect(() => {
        // If only one report of type, select it automatically
        if (directorates && projects && partnerOrgs && risks && financialRisks) {
            if (selectedSignOff.type === SignOffType.Directorate && directorates.length === 1)
                setSelectedSignOff({ type: SignOffType.Directorate, id: directorates[0].ID });
            if (selectedSignOff.type === SignOffType.Project && projects.length === 1)
                setSelectedSignOff({ type: SignOffType.Project, id: projects[0].ID });
            if (selectedSignOff.type === SignOffType.PartnerOrganisation && partnerOrgs.length === 1)
                setSelectedSignOff({ type: SignOffType.PartnerOrganisation, id: partnerOrgs[0].ID });
            if (selectedSignOff.type === SignOffType.Risk && risks.length === 1)
                setSelectedSignOff({ type: SignOffType.Risk, id: risks[0].ID });
            if (selectedSignOff.type === SignOffType.FinancialRisk && financialRisks.length === 1)
                setSelectedSignOff({ type: SignOffType.FinancialRisk, id: financialRisks[0].ID });
        }
    }, [directorates, projects, partnerOrgs, risks, financialRisks, selectedSignOff.type]);

    useEffect(() => {
        const getReportDates = async () => {
            if (selectedSignOff.type === SignOffType.Directorate) {
                setSelectedSignOffDates(await reportDueDatesService.getDirectorateReportDueDates(selectedSignOff.id, new Date(), selectedReportPeriod));
            }
            if (selectedSignOff.type === SignOffType.Portfolio) {
                setSelectedSignOffDates(await reportDueDatesService.getPortfolioReportDueDates(selectedSignOff.id, new Date, selectedReportPeriod));
            }
            if (selectedSignOff.type === SignOffType.Programme) {
                setSelectedSignOffDates(await reportDueDatesService.getProgrammeReportDueDates(selectedSignOff.id, new Date, selectedReportPeriod));
            }
            if (selectedSignOff.type === SignOffType.Project) {
                //console.log('props.defaultUpdatePeriod', props.defaultUpdatePeriod);
                if (isValidDate(props.defaultUpdatePeriod)) {
                    setSelectedSignOffDates(await reportDueDatesService.getProjectReportDueDates(selectedSignOff.id, props.defaultUpdatePeriod));
                } else {
                    //console.log('in else - props.defaultUpdatePeriod is not valid date', props.defaultUpdatePeriod);
                    //setSelectedSignOffDates(await reportDueDatesService.getProjectReportDueDates(selectedSignOff.id, new Date(), selectedReportPeriod));
                    const dueDates = await reportDueDatesService.getProjectReportDueDates(
                        selectedSignOff.id,
                        new Date(),
                        selectedReportPeriod
                    );

                    //console.log('Project Report Due Dates:', dueDates);

                    setSelectedSignOffDates(dueDates);
                }

            }
            if (selectedSignOff.type === SignOffType.PartnerOrganisation) {
                setSelectedSignOffDates(await reportDueDatesService.getPartnerOrganisationReportDueDates(selectedSignOff.id, new Date(), selectedReportPeriod));
            }
            if (selectedSignOff.type === SignOffType.Risk && risks) {
                setSelectedSignOffDates(await reportDueDatesService.getCorporateRiskReportDueDates(selectedSignOff.id, new Date(), selectedReportPeriod));
                //const risk = risks.find(r => r.ID === selectedSignOff.id);
                // if (risk) {
                //     setSelectedSignOffDates(await reportDueDatesService.getDirectorateReportDueDates(risk.DirectorateID, new Date(), selectedReportPeriod));                    
                // }
            }
            if (selectedSignOff.type === SignOffType.FinancialRisk) {
                setSelectedSignOffDates(await reportDueDatesService.getFinancialRiskReportDueDates(selectedSignOff.id, new Date(), selectedReportPeriod));
            }
        };
        getReportDates();
    }, [selectedSignOff.type, selectedSignOff.id, selectedReportPeriod, risks, reportDueDatesService, props.defaultUpdatePeriod]);

    const isValidDate = (date: any): date is Date => {
        return date instanceof Date && !isNaN(date.getTime());
    };


    return (
        <div className={`${styles.cr} ${styles.crList}`}>
            <CrLoadingOverlay isLoading={loading} opaque={true} />
            <ReportSelector
                {...props}
                reportPeriod={selectedReportPeriod}
                reportType={selectedSignOff.type}
                reportEntityId={selectedSignOff.id}
                directorates={LookupService.entitiesToSelectableOptions(directorates)}
                portfolios={LookupService.entitiesToSelectableOptions(portfolios)}
                programmes={LookupService.entitiesToSelectableOptions(programmes)}
                partnerOrganisations={LookupService.entitiesToSelectableOptions(partnerOrgs)}
                projects={LookupService.entitiesToSelectableOptions(projects)}
                risks={LookupService.entitiesToSelectableOptions(risks, { optionText: r => `${r.RiskCode} - ${r.Title}` })}
                financialRisks={LookupService.entitiesToSelectableOptions(financialRisks, { optionText: r => `${r.RiskCode} - ${r.Title}` })}
                onChangePeriod={setSelectedReportPeriod}
                onChangeType={t => setSelectedSignOff({ type: t, id: null })}
                onChangeEntity={e => setSelectedSignOff(sso => ({ type: sso.type, id: e }))}
                hideReportTypeSelector={hideReportTypeSelector}
                hidePeriodPicker={true}
            //hidePeriodPicker={isValidDate(props.defaultUpdatePeriod)}

            />
            <SignOffBuilder
                {...props}
                signOffType={selectedSignOff.type}
                signOffEntityId={selectedSignOff.id}
                period={selectedReportPeriod}
                reportDates={selectedSignOffDates}
                showPreviousUpdate={props.showPreviousUpdate}
            />
        </div>
    );
};
